import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import styles from "./GlyphPaywall.module.scss"

/**
 * GlyphPaywall (`/glyph`) — a self-contained FEATURE-SPECIFIC paywall for the
 * real Premium **Pattern Lock** feature of Private Photo Vault.
 *
 * Divergence brief: claude-paywalls/_ledger.md · concept doc: claude-paywalls/glyph.md
 *
 * The five prior feature-specific concepts each demo a different real feature —
 * Caught (break-in alerts), Recall (smart search), Reflex (face-down lock),
 * Slipstream (private browser), Safety Net (trash recovery). This is Pattern
 * Lock, and it is none of them on any axis:
 *   - Feature:     Pattern Lock — unlock the vault by drawing a secret gesture,
 *                  not a PIN someone can watch you type. (Untouched by all priors.)
 *   - Layout:      a LOCK-SCREEN / 3×3 PATTERN GRID over a private contact-sheet
 *                  of memories, with a two-step "set your glyph" header and a plan
 *                  footer. No prior paywall used a lockscreen / keypad / pattern
 *                  skeleton.
 *   - Interaction: you DRAW a connect-the-dots glyph (node-graph traversal with
 *                  snap + auto-through midpoints), then TRACE IT AGAIN to set it —
 *                  the app's real pattern-setup flow. First node-graph gesture and
 *                  first draw-then-confirm/commit mechanic (By Heart was a
 *                  free-form paint-over-a-shape; this is a discrete node path that
 *                  must be repeated).
 *   - Register:    intimate / secret / quietly-empowering — "a way in only your
 *                  hand knows." Not fear, luxury, cozy-nostalgia, playful-loud or
 *                  retro-mechanical.
 *   - Art:         LIGHT frosted-privacy-glass panel over a PHOTOGRAPHIC memory
 *                  grid; brand blue = the drawn glyph + the locked/sealed state,
 *                  amber = the un-set / open latch. Photos stay full colour; the
 *                  frost is the PROTECTED state, not a grey→colour reveal.
 *   - Value:      personal ACCESS-CONTROL — "the only way in is yours" — via
 *                  commitment/consistency + endowed-progress (you craft your own
 *                  glyph, then finish what you started) and honest contrast (a PIN
 *                  can be shoulder-surfed; a gesture is muscle memory).
 *
 * Honest & App Store compliant: Pattern Lock is a real Premium feature. Purchase
 * is never gated on the demo. No fake countdown / scarcity; the savings figure is
 * exact yearly-vs-monthly arithmetic. Yearly is the featured, default plan and
 * carries the most prominent price; monthly is small and secondary. Nothing is
 * deleted on expiration and the copy says so. Photo tiles are bundled demo
 * memories from `public/byheart/` (no network egress).
 */

// ── 3×3 grid geometry (fixed px so pointer hit-testing maps cleanly) ─────────
const BOX = 300 // grid svg box, px (square)
const PAD = 52 // inset of the outer node ring
const STEP = (BOX - 2 * PAD) / 2 // gap between node centres

// node index 0..8 → centre in the BOX coordinate space
const NODES = Array.from({ length: 9 }, (_, i) => {
  const r = Math.floor(i / 3)
  const c = i % 3
  return { x: PAD + c * STEP, y: PAD + r * STEP }
})

// nine bundled demo memories, one per node (always full colour)
const TILES = [
  "m12",
  "m03",
  "m06",
  "m16",
  "m08",
  "m05",
  "m13",
  "m18",
  "m07",
].map((s) => `/byheart/${s}.jpg`)

// android-style: dragging straight across a skipped node auto-includes it
const MIDPOINT: Record<string, number> = {
  "0-2": 1,
  "2-0": 1,
  "3-5": 4,
  "5-3": 4,
  "6-8": 7,
  "8-6": 7,
  "0-6": 3,
  "6-0": 3,
  "1-7": 4,
  "7-1": 4,
  "2-8": 5,
  "8-2": 5,
  "0-8": 4,
  "8-0": 4,
  "2-6": 4,
  "6-2": 4,
}

const HIT_R = 34 // px radius to register a node while dragging
const MIN_LEN = 4 // minimum nodes for a valid glyph (matches the real feature)

// a gentle sample glyph used for the auto-demo + the "use a sample" affordance
const SAMPLE = [0, 3, 6, 7, 4, 2]

type Phase = "draw" | "confirm" | "sealed"
type PlanKey = "yearly" | "monthly"

const clamp = (v: number, lo: number, hi: number) =>
  Math.max(lo, Math.min(hi, v))

const arraysEqual = (a: number[], b: number[]) =>
  a.length === b.length && a.every((v, i) => v === b[i])

// build an SVG path string through a list of node indices (+ optional live end)
const pathThrough = (idxs: number[], live?: { x: number; y: number }) => {
  if (idxs.length === 0) return ""
  const pts = idxs.map((i) => `${NODES[i].x},${NODES[i].y}`)
  let d = "M" + pts.join(" L")
  if (live) d += ` L${live.x},${live.y}`
  return d
}

const KeyGlyph = ({ size = 17 }: { size?: number }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden>
    <path
      d="M12 2l7 3v6c0 5-3 8-7 11-4-3-7-6-7-11V5l7-3z"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinejoin="round"
    />
    <circle cx={9.5} cy={10} r={1.6} fill="currentColor" />
    <circle cx={14.5} cy={10} r={1.6} fill="currentColor" />
    <circle cx={12} cy={14} r={1.6} fill="currentColor" />
    <path
      d="M9.5 10L14.5 10L12 14"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.4}
      strokeLinejoin="round"
    />
  </svg>
)

const Tick = ({ size = 13 }: { size?: number }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden>
    <path
      d="M5 12.5l4.2 4.2L19 7"
      fill="none"
      stroke="currentColor"
      strokeWidth={3}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const GlyphPaywall = () => {
  const { state } = useAppState()
  const reduceMotion = useReducedMotion()

  const premium = state.productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPrice = yearlyProduct
    ? formatCurrency(yearlyProduct.price, locale)
    : "$35.99"
  const yearlyPerMonth = yearlyProduct
    ? formatCurrency(yearlyProduct.price / 12, locale)
    : "$3.00"
  const monthlyPrice = monthlyProduct
    ? formatCurrency(monthlyProduct.price, locale)
    : "$13.99"
  const hasTrial = yearlyProduct?.hasTrial ?? true
  const trialDays = yearlyProduct?.introPricing?.period ?? 7

  const savingsPct = useMemo(() => {
    const y = yearlyProduct?.price ?? 35.99
    const m = monthlyProduct?.price ?? 13.99
    if (!m) return 0
    return Math.round((1 - y / (m * 12)) * 100)
  }, [yearlyProduct, monthlyProduct])

  const [plan, setPlan] = useState<PlanKey>("yearly")

  // ── glyph state ────────────────────────────────────────────────────────────
  const [phase, setPhase] = useState<Phase>("draw")
  const [path, setPath] = useState<number[]>([])
  const [firstGlyph, setFirstGlyph] = useState<number[]>([])
  const [live, setLive] = useState<{ x: number; y: number } | null>(null)
  const [shake, setShake] = useState(false)
  const [hint, setHint] = useState<string | null>(null)
  const [demoPath, setDemoPath] = useState<number[]>([])
  const [demoDone, setDemoDone] = useState(false)

  const svgRef = useRef<SVGSVGElement>(null)
  const draggingRef = useRef(false)
  const pathRef = useRef<number[]>([])
  const demoRafRef = useRef<number | null>(null)

  const sealed = phase === "sealed"

  // keep a ref of the live path so pointer handlers don't go stale
  useEffect(() => {
    pathRef.current = path
  }, [path])

  const cancelDemo = useCallback(() => {
    if (demoRafRef.current != null) {
      cancelAnimationFrame(demoRafRef.current)
      demoRafRef.current = null
    }
    setDemoPath([])
  }, [])

  // Auto-demo: a ghost hand traces a sample glyph once, then hands it over.
  useEffect(() => {
    if (reduceMotion) {
      setDemoDone(true)
      return
    }
    let cancelled = false
    const startTimer = window.setTimeout(() => {
      const perNode = 260
      const start = performance.now()
      const tick = (now: number) => {
        if (cancelled) return
        const elapsed = now - start
        const count = clamp(Math.floor(elapsed / perNode) + 1, 1, SAMPLE.length)
        setDemoPath(SAMPLE.slice(0, count))
        if (count < SAMPLE.length) {
          demoRafRef.current = requestAnimationFrame(tick)
        } else {
          // hold the finished ghost briefly, then dissolve it
          window.setTimeout(() => {
            if (cancelled) return
            setDemoPath([])
            setDemoDone(true)
          }, 620)
        }
      }
      demoRafRef.current = requestAnimationFrame(tick)
    }, 650)
    return () => {
      cancelled = true
      window.clearTimeout(startTimer)
      if (demoRafRef.current) cancelAnimationFrame(demoRafRef.current)
    }
  }, [reduceMotion])

  // client coords → BOX coordinate space
  const toBox = (clientX: number, clientY: number) => {
    const el = svgRef.current
    if (!el) return { x: 0, y: 0 }
    const r = el.getBoundingClientRect()
    return {
      x: ((clientX - r.left) / r.width) * BOX,
      y: ((clientY - r.top) / r.height) * BOX,
    }
  }

  const nearestNode = (x: number, y: number) => {
    for (let i = 0; i < NODES.length; i++) {
      const dx = x - NODES[i].x
      const dy = y - NODES[i].y
      if (dx * dx + dy * dy <= HIT_R * HIT_R) return i
    }
    return -1
  }

  const appendNode = (i: number) => {
    setPath((prev) => {
      if (prev.includes(i)) return prev
      const last = prev[prev.length - 1]
      const next = [...prev]
      if (last != null) {
        const mid = MIDPOINT[`${last}-${i}`]
        if (mid != null && !next.includes(mid)) next.push(mid)
      }
      next.push(i)
      return next
    })
  }

  const beginDraw = () => {
    if (sealed) return
    cancelDemo()
    setDemoDone(true)
    setHint(null)
    setShake(false)
    setPath([])
    draggingRef.current = true
  }

  const onPointerDown = (e: React.PointerEvent) => {
    if (sealed) return
    beginDraw()
    const p = toBox(e.clientX, e.clientY)
    const i = nearestNode(p.x, p.y)
    if (i >= 0) appendNode(i)
    setLive(p)
    svgRef.current?.setPointerCapture?.(e.pointerId)
  }

  const onPointerMove = (e: React.PointerEvent) => {
    if (!draggingRef.current) return
    const p = toBox(e.clientX, e.clientY)
    const i = nearestNode(p.x, p.y)
    if (i >= 0) appendNode(i)
    setLive(p)
  }

  const finishGlyph = useCallback(
    (drawn: number[]) => {
      if (phase === "draw") {
        if (drawn.length < MIN_LEN) {
          setHint(`Connect at least ${MIN_LEN} dots to make a glyph.`)
          setPath([])
          return
        }
        setFirstGlyph(drawn)
        native.logMixpanelEvent("html_glyph_drawn", { length: drawn.length })
        // brief beat on the drawn glyph, then ask to confirm
        window.setTimeout(() => {
          setPath([])
          setPhase("confirm")
          setHint(null)
        }, 480)
      } else if (phase === "confirm") {
        if (arraysEqual(drawn, firstGlyph)) {
          native.logMixpanelEvent("html_glyph_set", {})
          setPhase("sealed")
          setHint(null)
        } else {
          setShake(true)
          setHint("That's a different shape — trace your glyph again.")
          window.setTimeout(() => {
            setShake(false)
            setPath([])
          }, 420)
        }
      }
    },
    [phase, firstGlyph],
  )

  const endDraw = () => {
    if (!draggingRef.current) return
    draggingRef.current = false
    setLive(null)
    finishGlyph(pathRef.current)
  }

  const useSample = () => {
    cancelDemo()
    setDemoDone(true)
    setShake(false)
    setFirstGlyph(SAMPLE)
    setPath(SAMPLE)
    setPhase("sealed")
    setHint(null)
    native.logMixpanelEvent("html_glyph_sample", {})
  }

  const resetGlyph = () => {
    cancelDemo()
    setPhase("draw")
    setPath([])
    setFirstGlyph([])
    setHint(null)
    setShake(false)
    setDemoDone(true)
  }

  const choosePlan = (key: PlanKey) => {
    setPlan(key)
    native.logMixpanelEvent("html_pricing_toggle", { plan: key })
  }

  const handleClose = () => native.postNativeEvent("close")

  const purchase = () => {
    const product = plan === "yearly" ? yearlyProduct : monthlyProduct
    if (product) native.subscribe(product)
  }

  // ── derived copy ────────────────────────────────────────────────────────────
  const stepLabel =
    phase === "sealed"
      ? "Your glyph is set"
      : phase === "confirm"
        ? "Step 2 of 2 · Trace it again to set it"
        : "Step 1 of 2 · Draw your glyph"

  const stepBody =
    phase === "sealed"
      ? "Locked to your hand. Only this shape opens your vault — no one can watch you type it."
      : phase === "confirm"
        ? "Draw the same shape once more so it becomes muscle memory."
        : "Connect the dots into a shape only you know. Drag across your memories to draw."

  const activeGlyph = demoPath.length > 0 ? demoPath : path
  const visited = new Set(sealed ? firstGlyph : activeGlyph)

  const ctaLabel =
    plan === "yearly"
      ? hasTrial
        ? `Start ${trialDays} days free`
        : "Lock in my vault"
      : "Choose monthly"

  const ctaSub =
    plan === "yearly"
      ? hasTrial
        ? `${trialDays} days free, then ${yearlyPrice}/yr · cancel anytime`
        : `${yearlyPrice}/yr · auto-renews · cancel anytime`
      : `${monthlyPrice}/mo · auto-renews · cancel anytime`

  return (
    <div className={styles.page}>
      <div className={styles.grain} aria-hidden />

      <button className={styles.close} onClick={handleClose} aria-label="Close">
        <svg viewBox="0 0 24 24" width={16} height={16} aria-hidden>
          <path
            d="M6 6l12 12M18 6L6 18"
            stroke="currentColor"
            strokeWidth={2.2}
            strokeLinecap="round"
          />
        </svg>
      </button>

      <header className={styles.head}>
        <div className={styles.brand}>
          <span className={styles.brandKey} aria-hidden>
            <KeyGlyph size={15} />
          </span>
          <span>Private Photo Vault</span>
          <span className={styles.premiumPip}>Premium</span>
        </div>
        <h1 className={styles.title}>The only way in is yours.</h1>
        <p className={styles.sub}>
          Open your vault with a <b>Pattern Lock</b> — a shape your hand
          remembers, that no one can shoulder-surf or guess.
        </p>
      </header>

      {/* ── The lock panel ──────────────────────────────────────────────── */}
      <div
        className={`${styles.panel} ${sealed ? styles.panelSealed : ""} ${
          shake ? styles.panelShake : ""
        }`}
      >
        <div className={styles.stepRow}>
          <span
            className={`${styles.stepDot} ${
              phase !== "draw" ? styles.stepDotDone : ""
            }`}
            aria-hidden
          />
          <span
            className={`${styles.stepDot} ${
              phase === "sealed" ? styles.stepDotDone : ""
            }`}
            aria-hidden
          />
          <span className={styles.stepLabel}>{stepLabel}</span>
        </div>

        <div
          className={styles.grid}
          role="application"
          aria-label="Draw your unlock pattern by connecting the dots"
        >
          {/* the memory tiles, one per node */}
          <div className={styles.tiles} aria-hidden>
            {TILES.map((src, i) => (
              <span
                key={i}
                className={`${styles.tile} ${
                  visited.has(i) ? styles.tileOn : ""
                }`}
                style={{
                  left: `${(NODES[i].x / BOX) * 100}%`,
                  top: `${(NODES[i].y / BOX) * 100}%`,
                  backgroundImage: `url(${src})`,
                }}
              />
            ))}
          </div>

          {/* the drawing surface + glyph line */}
          <svg
            ref={svgRef}
            className={styles.glyphSvg}
            viewBox={`0 0 ${BOX} ${BOX}`}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={endDraw}
            onPointerCancel={endDraw}
          >
            <defs>
              <linearGradient id="glyphLine" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0" stopColor="#4f7bff" />
                <stop offset="1" stopColor="#1c46c9" />
              </linearGradient>
            </defs>

            {/* the ghost demo trace */}
            {demoPath.length > 1 && (
              <path
                className={styles.ghostLine}
                d={pathThrough(demoPath)}
                fill="none"
              />
            )}

            {/* the live / set glyph line */}
            {(sealed ? firstGlyph : path).length > 0 && (
              <>
                <path
                  className={styles.glyphGlow}
                  d={pathThrough(
                    sealed ? firstGlyph : path,
                    sealed ? undefined : (live ?? undefined),
                  )}
                  fill="none"
                />
                <path
                  className={styles.glyphLine}
                  d={pathThrough(
                    sealed ? firstGlyph : path,
                    sealed ? undefined : (live ?? undefined),
                  )}
                  fill="none"
                />
              </>
            )}

            {/* node dots */}
            {NODES.map((n, i) => {
              const on = visited.has(i)
              return (
                <g key={i}>
                  <circle
                    cx={n.x}
                    cy={n.y}
                    r={on ? 9 : 5.5}
                    className={`${styles.node} ${on ? styles.nodeOn : ""}`}
                  />
                  {on && (
                    <circle
                      cx={n.x}
                      cy={n.y}
                      r={4}
                      className={styles.nodeCore}
                    />
                  )}
                </g>
              )
            })}
          </svg>

          {/* the frosted "locked" overlay, on when sealed */}
          <div className={styles.frost} aria-hidden={!sealed}>
            <div className={styles.frostBadge}>
              <span className={styles.frostLock}>
                <KeyGlyph size={22} />
              </span>
              <b>Locked to your glyph</b>
              <span className={styles.frostTick}>
                <Tick size={11} /> Only your shape opens it
              </span>
            </div>
          </div>
        </div>

        <p
          className={`${styles.hint} ${hint ? styles.hintWarn : ""}`}
          aria-live="polite"
        >
          {hint ?? stepBody}
        </p>

        <div className={styles.panelActions}>
          {!sealed ? (
            <button className={styles.ghostBtn} onClick={useSample}>
              Use a sample glyph
            </button>
          ) : (
            <button className={styles.ghostBtn} onClick={resetGlyph}>
              Draw a different glyph
            </button>
          )}
        </div>
      </div>

      {/* what Pattern Lock is, in the honest Premium bundle */}
      <ul className={styles.keeps}>
        {[
          "A private gesture — not a PIN anyone can watch",
          "Your memories stay encrypted & backed up",
          "In sync, privately, on every device",
        ].map((t) => (
          <li key={t}>
            <span className={styles.keepDot} aria-hidden>
              <Tick size={10} />
            </span>
            {t}
          </li>
        ))}
      </ul>

      {/* ── Plan choice — yearly featured, monthly small & secondary ──────── */}
      <div className={styles.plans}>
        <button
          className={`${styles.planYear} ${
            plan === "yearly" ? styles.planSel : ""
          }`}
          onClick={() => choosePlan("yearly")}
          aria-pressed={plan === "yearly"}
        >
          <span className={styles.planMain}>
            <span className={styles.planName}>Yearly</span>
            <span className={styles.planNote}>
              Best value · lock it in for a year
            </span>
          </span>
          <span className={styles.planPrice}>
            <span className={styles.planBig}>{yearlyPrice}</span>
            <span className={styles.planPer}>/yr</span>
            <span className={styles.planSmall}>
              {yearlyPerMonth}/mo
              {savingsPct > 0 ? ` · save ${savingsPct}%` : ""}
            </span>
          </span>
          {savingsPct > 0 && (
            <span className={styles.saveBadge}>SAVE {savingsPct}%</span>
          )}
        </button>

        <button
          className={`${styles.planMonth} ${
            plan === "monthly" ? styles.planMonthSel : ""
          }`}
          onClick={() => choosePlan("monthly")}
          aria-pressed={plan === "monthly"}
        >
          <span className={styles.planMonthName}>Monthly</span>
          <span className={styles.planMonthPrice}>{monthlyPrice}/mo</span>
        </button>
      </div>

      <button className={styles.cta} onClick={purchase}>
        <span className={styles.ctaLabel}>{ctaLabel}</span>
        <span className={styles.ctaSub}>{ctaSub}</span>
      </button>

      <p className={styles.reassure}>
        Your photos are always yours. Pattern Lock only decides who gets in —
        nothing is ever deleted.
      </p>

      <div className={styles.legal}>
        <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
        <span aria-hidden>·</span>
        <button onClick={() => native.postNativeEvent("privacy")}>
          Privacy
        </button>
        <span aria-hidden>·</span>
        <button onClick={() => native.postNativeEvent("already_purchased")}>
          Restore
        </button>
      </div>
    </div>
  )
}

export default GlyphPaywall
